import { useState, useMemo, useCallback } from 'react';
import useResource from 'hooks/useResource';
import useSearcher from 'hooks/useSearcher';
import useForm, { FormMode } from 'hooks/useForm';

const useAdminResource = ({
  resourceName,
  resourceNamePlural,
  resource,
  searchFields = [],
  transformFormData,
}) => {
  const { state, actions } = useResource(resource, { prefetch: true });
  const [searchVal, setSearchVal] = useState('');
  const searcher = useSearcher(searchFields, state.list);
  const [form] = useForm({
    onSubmit: async (formData) => {
      const isCreate = form.mode === FormMode.create;
      if (typeof transformFormData === 'function') {
        formData = transformFormData(formData) || formData;
      }

      if (isCreate) {
        await actions.create(formData).then(() => {
          form.reset();
          reload();
        });
      } else {
        await actions.update(formData).then(() => {
          form.reset();
          reload();
        });
      }
    }
  });

  const dataList = useMemo(() => {
    return searcher.search(searchVal);
  }, [searcher, searchVal])

  const strings = useMemo(() => {
    return {
      get addButton() {
        return `Add new ${resourceName}`;
      },
      get formTitle() {
        if (form.mode === FormMode.create) {
          return `Add new ${resourceName}`;
        } else {
          return `Update ${resourceName}`;
        }
      },
      get formSubmitButton() {
        if (form.mode === FormMode.create) {
          return `Add ${resourceName}`;
        } else {
          return `Update ${resourceName}`;
        }
      },
      get searchPlaceholder() {
        return `Search ${resourceNamePlural || resourceName + 's'}`;
      }
    };
  }, [resourceName, resourceNamePlural, form.mode]);

  const reload = useCallback(() => {
    actions.getAll();
  }, [actions]);

  const onCreate = (extraValues) => {
    form.create();
    if (extraValues) {
      form.setExtraValues(extraValues);
    }
  };

  const onEdit = (formData) => {
    form.edit(formData);
    form.setExtraValues(formData);
  };

  const onDelete = (id) => {
    actions.delete(id);
  };

  return {
    strings: strings,
    form: form,
    list: dataList,
    isLoading: state.isLoading,
    searchVal: searchVal,
    search: setSearchVal,
    onCreate,
    onEdit,
    onDelete,
    reload,
  };
};

export default useAdminResource;
